import {ColumnsType} from 'antd/lib/table';
import {Chart} from '@antv/g2';
import {FC, useEffect, useRef, useState} from 'react';
import {Col, DatePicker, Row, Table} from 'antd';
import {LOCATIONS_COLORS, PALETTE} from './Stats';
import moment from 'moment';
import {useG2Chart} from './hooks';

const {RangePicker} = DatePicker;

export interface OutputData {
  location: string;
  pieceType: string;
  number: number | null;
  date: string;
}

export interface OrdersData {
  location: string;
  total: number | null;
  industry: number | null;
  audio: number | null;
  audioWeb: number | null;
  date: string;
}

const getChartData = (
  data: (OrdersData | OutputData)[],
): (OrdersData | OutputData)[] => {
  return data.filter((obj) => obj.location !== null);
};

const createOverallOutputsPieChart = (
  container: HTMLDivElement,
  data: any[],
) => {
  const chart = new Chart({
    container,
    width: 500,
    height: 500,
  });

  chart.data(getChartData(data));

  chart.coordinate('theta', {
    radius: 0.75,
  });

  chart.scale('number', {});

  chart.tooltip({
    title: 'pieceType',
    showMarkers: false,
  });
  chart.legend(false);
  chart
    .interval()
    .position('number')
    .label('pieceType', {
      content: (data) => `${data.location}: \n ${data.pieceType}` 
    })
    .color('location*pieceType', (location: string, pieceType: string) => PALETTE[location][pieceType])
    .style({
      stroke: 'white',
      lineWidth: 1,
    })
    .adjust('stack');
  chart.render();

  return chart;
};

const createOverallOrdersPieChart = (
  container: HTMLDivElement,
  data: any[],
) => {
  const chart = new Chart({
    container,
    width: 500,
    height: 500,
  });

  chart.data(getChartData(data));

  chart.coordinate('theta', {
    radius: 0.75,
  });

  chart.scale('total', {});

  chart.tooltip({
    showTitle: false,
    showMarkers: false,
  });

  chart
    .interval()
    .position('total')
    .color('location', (val) => LOCATIONS_COLORS[val])
    .label('total', {
      layout: [{type: 'limit-in-plot', cfg: {action: 'ellipsis'}}],
      content: (data) => {
        return `${data.location}: ${data.total}`;
      },
    })
    .adjust('stack');
  chart.render();

  return chart;
};

const usePieChart = <T,>(
  ref: React.RefObject<HTMLDivElement>,
  type: 'orders' | 'outputs',
) => {
  const [chartData, setChartData] = useState<T[]>([]);
  const [chart, setChart] = useState<Chart>();

  useG2Chart(ref, chartData, (container, data) => {
    let pieChart: Chart;
    if (type === 'orders') {
      pieChart = createOverallOrdersPieChart(container, data);
    } else {
      pieChart = createOverallOutputsPieChart(container, data);
    }
    return pieChart;
  });

  return {barChart: chart, setChartData};
};

export const DateToDateSituation: FC<{
  initialOrders: OrdersData[];
  initialOuputs: OutputData[];
}> = ({initialOrders, initialOuputs}) => {
  const ref1 = useRef<HTMLDivElement>(null);
  const ref2 = useRef<HTMLDivElement>(null);
  const [dateRange, setDateRange] = useState<[string, string]>([
    '2024-01-01',
    '2024-03-31',
  ]);
  const [ordersData, setOrdersData] = useState<OrdersData[]>([]);
  const [outputData, setOutputData] = useState<OutputData[]>([]);

  const {setChartData: setOrdersChartData} = usePieChart(ref1, 'orders');
  const {setChartData: setOutputsChartData} = usePieChart(ref2, 'outputs');

  useEffect(() => {
    const filteredData = initialOrders.filter((obj) => {
      const dateFilter =
        moment(obj.date).isAfter(moment(dateRange[0])) &&
        moment(obj.date).isBefore(moment(dateRange[1]));
      return dateFilter;
    });
    setOrdersData(filteredData);
    setOrdersChartData(filteredData);
  }, [initialOrders, dateRange, setOrdersChartData, setOrdersData]);

  useEffect(() => {
    const filteredData = initialOuputs.filter((obj) => {
      const dateFilter =
        moment(obj.date).isAfter(moment(dateRange[0])) &&
        moment(obj.date).isBefore(moment(dateRange[1]));
      return dateFilter;
    });
    setOutputData(filteredData);
    setOutputsChartData(filteredData);
  }, [initialOuputs, dateRange, setOutputsChartData, setOutputData]);

  const orderColumns: ColumnsType<OrdersData> = [
    {title: 'Commandes entrées', key: 'orders', dataIndex: 'location'},
    {title: 'Embouts', key: 'embouts', dataIndex: 'total'},
    {title: 'Industrie', key: 'industrie', dataIndex: 'industry'},
    {title: 'Audio', key: 'audio', dataIndex: 'audio'},
    {title: '%Audio web', key: 'audioweb', dataIndex: 'audioWeb'},
  ];

  const outputColumns: ColumnsType<OutputData> = [
    {
      title: 'Détail embouts sortis par site/atelier',
      key: 'outputs',
      dataIndex: 'location',
      render: (value, record) => {
        return `${value}: ${record.pieceType}`;
      },
    },
    {title: 'Embouts', key: 'embouts', dataIndex: 'number'},
  ];
  return (
    <>
      <Row style={{marginBottom: 10}} gutter={10}>
        <Col>
          <RangePicker
            onChange={(values) => {
              if (values)
                setDateRange([
                  values[0]?.format('YYYY-MM-DD') ?? '2024-01-01',
                  values[1]?.format('YYYY-MM-DD') ?? '2024-03-31',
                ]);
            }}
            value={[moment(dateRange[0]), moment(dateRange[1])]}
            format={'DD/MM/YYYY'}
          />
        </Col>
      </Row>
      <Table
        columns={orderColumns}
        dataSource={ordersData}
        size="small"
        onRow={(record) => ({
          style: {backgroundColor: LOCATIONS_COLORS[record.location]},
        })}
      />
      <Table
        columns={outputColumns}
        dataSource={outputData}
        size="small"
        onRow={(record) => ({
          style: {backgroundColor: PALETTE[record.location][record.pieceType]},
        })}
      />
      <Row></Row>
      <Row>
        <Col>
          <div ref={ref1}></div>
          <h2>Total commandes</h2>
        </Col>
        <Col>
          <div ref={ref2}></div>
          <h2>Détails embouts sortis</h2>
        </Col>
      </Row>
    </>
  );
};
